/**
 * Labstep
 *
 * @module components/Folder/Breadcrumb
 * @desc Breadcrumb for Folder
 */

import { FolderActionCreateContainer as FolderActionCreate } from 'labstep-web/components/Folder/Action/Create';
import { FolderBreadcrumbItem } from 'labstep-web/components/Folder/Breadcrumb/Item';
import FolderPaneList from 'labstep-web/components/Folder/Pane/List';
import { ICONS } from 'labstep-web/constants/icons';
import { withActiveGroup } from 'labstep-web/containers/ActiveGroup';
import { ReadOnMountHOC } from 'labstep-web/hoc/ReadOnMount';
import { EntityView } from 'labstep-web/models/entity-view.model';
import { ExperimentWorkflow } from 'labstep-web/models/experiment-workflow.model';
import { Folder, FolderType } from 'labstep-web/models/folder.model';
import { ProtocolCollection } from 'labstep-web/models/protocol-collection.model';
import { ScreensIndexBreadcrumb } from 'labstep-web/screens/Breadcrumb';
import { LocationService } from 'labstep-web/services/location.service';
import { navigation } from 'labstep-web/services/navigation';
import type React from 'react';
import { withRouter } from 'react-router-dom';
import styles from './styles.module.scss';
import type { FolderBreadcrumbProps } from './types';

export const FolderBreadcrumb: React.FC<FolderBreadcrumbProps> = ({
  type,
  indexRoute,
  activeGroup,
  history,
  location,
}) => {
  let entityName = '';

  if (type === FolderType.entity_view) {
    entityName = EntityView.getHumanReadableEntityName(true, true);
  }
  if (type === FolderType.experiment_workflow) {
    entityName = ExperimentWorkflow.getHumanReadableEntityName(
      true,
      true,
    );
  }
  if (type === FolderType.protocol_collection) {
    entityName = ProtocolCollection.getHumanReadableEntityName(
      true,
      true,
    );
  }

  const icon = ICONS[type].primary;

  const screensIndexBreadcrumb = (
    <ScreensIndexBreadcrumb
      text={`All ${entityName}`}
      onClick={() => {
        if (activeGroup) {
          history.push(
            navigation.get(indexRoute, {
              id: activeGroup.id,
            }),
          );
        }
      }}
      rightMobileIcon={ICONS.folder.primary}
      dropdownContent={({ header }) => (
        <div className={styles.contentContainer}>
          {header}
          <FolderPaneList folderType={type} />
          <FolderActionCreate folderType={type} />
        </div>
      )}
      icon={icon}
      right={({ searchParams }) => {
        return searchParams.folder_id || searchParams.is_unsorted ? (
          <FolderBreadcrumbItem
            searchParams={searchParams}
            folderType={type}
          />
        ) : null;
      }}
    />
  );

  const folderId = LocationService.getQueryParameter(
    location,
    'folder_id',
  );
  if (folderId) {
    return (
      <ReadOnMountHOC
        type="entities"
        entityName={Folder.entityName}
        params={{
          get_single: 1,
          id: folderId,
        }}
        loading={false}
      >
        {() => screensIndexBreadcrumb}
      </ReadOnMountHOC>
    );
  }

  return screensIndexBreadcrumb;
};

export default withActiveGroup(withRouter(FolderBreadcrumb));
